// SocketHandler.ts (listen-only socket; HTTP publish; no third-party flow)
import { CreateLogger, URLFromAddress } from "@swivl-mono/helpers";
import { Channel, Log, MessageAuth, MessageInterface, MessageType } from "@swivl-mono/types";
import { browserName, browserVersion } from "react-device-detect";
import { Socket, io } from "socket.io-client";

import { Config } from "../Config/Config";
import SwivlChatInterface from "../interface/SwivlChatInterface";

const log = console.log; // CreateLogger(Log.CHAT, "SocketHandler");

function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function ensureHttpBase(urlOrHost: string): string {
  if (!urlOrHost) return "";
  if (urlOrHost.startsWith("http://") || urlOrHost.startsWith("https://")) return urlOrHost;
  // default to https for Messaging API calls
  return `https://${urlOrHost}`;
}
function ensureWsBase(urlOrHost: string): string {
  if (!urlOrHost) return "";
  if (urlOrHost.startsWith("http://") || urlOrHost.startsWith("https://")) return urlOrHost;
  if (urlOrHost.startsWith("ws://") || urlOrHost.startsWith("wss://")) return urlOrHost.replace("ws://", "http://").replace("wss://", "https://");
  // Socket.IO uses HTTP(S) base and upgrades to WS
  return `https://${urlOrHost}`;
}

export interface SocketHandlerInterface {
  delegate: SwivlChatInterface;
  hasConnected: boolean;
  socket?: Socket;
  seenInterval?: NodeJS.Timer;

  connect(): void;
  disconnect(): void;
  sendMessage(message: MessageInterface): Promise<void>; // HTTPS → Messaging
}

export class SocketHandler implements SocketHandlerInterface {
  delegate: SwivlChatInterface;
  hasConnected = false;
  socket?: Socket;
  seenInterval?: NodeJS.Timer;
  shouldStayClose = false;

  constructor(delegate: SwivlChatInterface) {
    this.delegate = delegate;
  }

  connect(): void {
    if (this.shouldStayClose) return;
    if (Config.isReadOnly) return;

    console.log("SocketHandler.connect", {
      server: this.delegate.props.server,
      webSocketServer: this.delegate.props.webSocketServer,
      messagingServer: this.delegate.props.messagingServer
    });
    const token = (this.delegate.state?.auth as MessageAuth | undefined)?.token;
    if (!token) {
      log("connect skipped: missing JWT token in delegate.state.auth.token");
      return;
    }

    try {
      const channelsBase = this.delegate.props.webSocketServer
        ? ensureWsBase(this.delegate.props.webSocketServer)
        : ensureWsBase(this.delegate.props.server);

      this.socket = io(channelsBase, {
        transports: ["websocket"], // avoid polling/stickiness
        auth: { token },
        path: "/ws",

        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
        timeout: 10000
      });

      this.socket.on("connect", () => this.socketOnOpen());
      this.socket.on("event", (msg: MessageInterface) => this.socketOnEvent(msg));
      this.socket.on("connect_error", (err: Error) => this.socketOnError(err));
      this.socket.on("disconnect", (reason: string) => this.socketOnClose(reason));
    } catch (error) {
      const rando = getRandomInt(3, 15);
      setTimeout(() => !this.hasConnected && this.connect(), rando * 1000);
      return;
    }
  }

  disconnect(shouldStayClose: boolean = true): void {
    this.shouldStayClose = shouldStayClose;

    if (this.seenInterval) {
      clearInterval(this.seenInterval);
      this.seenInterval = undefined;
    }

    if (this.socket) {
      // Only emit unsubscribe if the socket is actually connected
      if (this.socket.connected) {
        try {
          this.socket.emit("unsubscribe", [{ channel: this.delegate.props.channel }]);
        } catch {}
      }

      // Remove all listeners before disconnecting to prevent callbacks during teardown
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = undefined;
    }

    this.hasConnected = false;
  }

  // --------- Socket (listen-only) ---------

  private socketOnOpen(): void {
    if (!this.hasConnected) this.delegate.onConnect();
    this.hasConnected = true;

    // subscribe by channel only; server derives (orgId,userId) from JWT + merge map
    const channel = this.delegate.props.channel || ("web" as Channel);
    this.socket?.emit("subscribe", [{ channel }]);
    log("subscribe", { channel });

    // optional "seen" loop over HTTPS (not over socket)
    if (!this.seenInterval && !this.delegate.props.transcriptOnly) {
      this.seenInterval = setInterval(() => this.sendSeen(), 7000);
    }
  }

  private socketOnEvent(message: MessageInterface): void {
    // Server emits raw MessageInterface (including UserShouldMerge payloads).
    this.delegate.messageHandler.handleMessage(message);
  }

  private socketOnError(e: any): void {
    if (!this.delegate.props.canTakeOver && (e?.message === "jwt expired" || e?.message === "jwt malformed")) {
      this.disconnect(false);
      this.delegate.clearAuthAndCreateNewUser();
      return;
    }
    log("socket error", e?.message || e);
  }

  private socketOnClose(reason: string): void {
    const rando = getRandomInt(3, 15);
    setTimeout(() => !this.hasConnected && this.connect(), rando * 1000);
  }

  // --------- Messaging HTTPS (publish) ---------

  private async postToMessaging(message: Partial<MessageInterface>) {
    const token = (this.delegate.state?.auth as MessageAuth | undefined)?.token;
    if (!token) {
      log("publish aborted: missing token");
      return;
    }
    const base = ensureHttpBase(Config.messagingServer || this.delegate.props.messagingServer || this.delegate.props.server);
    // console.log(
    //   "Config.messagingServer || this.delegate.props.messagingServer || this.delegate.props.server :",
    //   Config.messagingServer,
    //   this.delegate.props.messagingServer,
    //   this.delegate.props.server
    // );
    const body = {
      message,
      token: this.delegate.state?.auth?.token,
      channel: this.delegate.props.channel,
      currentURL: URLFromAddress({
        origin: window.location.origin,
        port: window.location.port,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        pathname: window.location.pathname,
        hash: window.location.hash,
        isMobile: this.delegate.props.isMobile
      }),
      browser: `${browserName} - ${browserVersion}`
    };

    const res = await fetch(`${base}/api/message/from-client`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      log("publish failed", { status: res.status, text });
      throw new Error(`Publish failed: ${res.status}`);
    }
    return res.json();
  }

  private uuid(): string {
    try {
      // @ts-ignore
      if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    } catch {}
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private async sendSeen() {
    if (this.socket?.connected && this.delegate.state) {
      this.socket?.emit("seen", {
        orgId: this.delegate.state?.auth?.orgId,
        userId: this.delegate.state?.auth?.userId,
        channel: this.delegate.props.channel || Channel.WEB
      });
    }
  }

  async sendMessage(message: MessageInterface): Promise<void> {
    if (this.delegate.props.transcriptOnly) return;

    const auth = this.delegate.state?.auth as MessageAuth | undefined;
    const channel = message.channel || (this.delegate.props.channel as Channel);

    const payload: MessageInterface = {
      ...message,
      uuid: (message as any).uuid || this.uuid(),
      channel,
      orgId: auth?.orgId ?? (message as any).orgId, // server overwrites from token anyway
      userId: auth?.userId ?? (message as any).userId,
      createdAt: message.createdAt || new Date().toISOString()
    } as any;

    await this.postToMessaging(payload);

    if (this.delegate.props.onMessageSent) {
      this.delegate.props.onMessageSent(message);
    }
  }
}
